import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { app } from '../firebase';
import { API_CONFIG, displayWeeksMetRequirement, getApiUrl } from '../config/apiConfig';

const Account: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [tracked, setTracked] = useState<any[]>([]);
  const [trackedLoading, setTrackedLoading] = useState(true);
  const [removing, setRemoving] = useState<string | null>(null);
  const [mainCharacterName, setMainCharacterName] = useState('');
  const [mainCharacterNameEditing, setMainCharacterNameEditing] = useState(false);
  const [mainCharacterNameLoading, setMainCharacterNameLoading] = useState(false);
  const [mainCharacterNameError, setMainCharacterNameError] = useState('');
  const [mainCharacterNameSuccess, setMainCharacterNameSuccess] = useState('');

  // Fetch user data including main character name
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      const db = getFirestore(app);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setMainCharacterName(userData.mainCharacterName || '');
      }
    };
    fetchUserData();
  }, [user]);

  // Fetch tracked characters
  useEffect(() => {
    const fetchTracked = async () => {
      if (!user) return;
      setTrackedLoading(true);
      const db = getFirestore(app);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const trackedList = userDoc.exists() && userDoc.data().trackedCharacters ? userDoc.data().trackedCharacters : [];
      
      // First fetch all weekly counts and weeks met requirement from the new API
      let weeklyCounts = new Map<string, number>();
      let weeksMetRequirement = new Map<string, number>();
      try {
        const countsUrl = getApiUrl('guild-mythic-counts');
        
        const response = await fetch(countsUrl, {
          headers: { 'X-API-Key': API_CONFIG.API_KEY },
          method: 'GET',
          mode: 'cors',
          signal: AbortSignal.timeout(30000), // 30 second timeout
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.data && Array.isArray(data.data)) {
            data.data.forEach((player: any) => {
              if (player.Name) {
                const key = player.Name.toLowerCase();
                // Store weekly count (capped at 8)
                if (player.count !== undefined) {
                  weeklyCounts.set(key, Math.min(player.count, 8));
                }
                // Store weeks met requirement
                if (player.weeks_with_4_plus_runs !== undefined) {
                  weeksMetRequirement.set(key, displayWeeksMetRequirement(player.weeks_with_4_plus_runs));
                }
              }
            });
          }
        }
      } catch (error) {
        console.warn('Failed to fetch weekly data for tracked characters:', error);
      }
      
      // Fetch character data from leadership, roster, and heroicRoster using queries
      const charPromises = trackedList.map(async (char: any) => {
        const normRealm = (char.realm || '').toLowerCase().replace(/ /g, '-');
        const normRegion = (char.region || '').toLowerCase().replace(/ /g, '-');
        // Helper to query a collection for a character
        const findChar = async (coll: string) => {
          const qRef = query(
            collection(db, coll),
            where('name', '==', char.name),
            where('realm', '==', normRealm),
            where('region', '==', normRegion)
          );
          const snap = await getDocs(qRef);
          return snap.docs.length > 0 ? { id: snap.docs[0].id, ...snap.docs[0].data() } : null;
        };
        // Try leadership first
        let data = await findChar('leadership');
        if (data) data = { ...(data as any), team: 'mythic', isLeadership: true };
        if (!data) {
          data = await findChar('roster');
          if (data) data = { ...(data as any), team: (data as any).team || 'mythic', isLeadership: false };
        }
        if (!data) {
          data = await findChar('heroicRoster');
          if (data) data = { ...(data as any), team: (data as any).team || 'heroic', isLeadership: false };
        }
        
        if (data) {
          // Enrich with weekly data from the new API
          const weeklyCount = weeklyCounts.get(char.name.toLowerCase()) ?? 0;
          const weeksMet = weeksMetRequirement.get(char.name.toLowerCase()) ?? 0;
          
          return { 
            ...data, 
            name: char.name, 
            realm: char.realm, 
            region: char.region,
            mythicPlusThisWeek: weeklyCount,
            weeksMetRequirement: weeksMet
          };
        }
        return null;
      });
      const chars = (await Promise.all(charPromises)).filter(Boolean);
      setTracked(chars);
      setTrackedLoading(false);
    };
    fetchTracked();
  }, [user]);

  // Remove tracked character
  const handleRemoveTracked = async (name: string, realm: string, region: string) => {
    if (!user) return;
    setRemoving(`${name}|${realm}|${region}`);
    const db = getFirestore(app);
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    const trackedList = userDoc.exists() && userDoc.data().trackedCharacters ? userDoc.data().trackedCharacters : [];
    const newList = trackedList.filter((c: any) => !(c.name === name && c.realm === realm && c.region === region));
    await updateDoc(userRef, { trackedCharacters: newList });
    setTracked(tracked => tracked.filter(c => !(c.name === name && c.realm === realm && c.region === region)));
    setRemoving(null);
  };

  useEffect(() => {
    if (!user && !loading) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const handleMainCharacterNameUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    const trimmedName = mainCharacterName.trim();
    if (!trimmedName) {
      setMainCharacterNameError('Main character name cannot be blank.');
      return;
    }
    
    setMainCharacterNameLoading(true);
    setMainCharacterNameError('');
    setMainCharacterNameSuccess('');
    
    try {
      const db = getFirestore(app);
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { mainCharacterName: trimmedName });
      setMainCharacterNameSuccess('Main character name updated successfully!');
      setMainCharacterNameEditing(false);
      setTimeout(() => setMainCharacterNameSuccess(''), 3000);
    } catch (err: any) {
      setMainCharacterNameError(err.message || 'Failed to update main character name.');
    } finally {
      setMainCharacterNameLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setPasswordLoading(true);
    try {
      const auth = getAuth();
      if (!auth.currentUser || !user || !user.email) throw new Error('No authenticated user.');
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      setSuccess('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) return null;
  if (!user) return null;

  return (
    <div className="page-container">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        <h1 className="text-4xl font-bold mb-8 text-center gradient-text">My Account</h1>
        <div className="glass p-8 rounded-xl mb-8">
          <h2 className="text-2xl font-semibold mb-4">Account Info</h2>
          <div className="mb-2"><span className="font-medium">Email:</span> {user.email}</div>
          {user.username && <div className="mb-2"><span className="font-medium">Username:</span> {user.username}</div>}
          
          {/* Main Character Name Section */}
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <label className="font-medium">Main Character Name:</label>
              {!mainCharacterNameEditing && (
                <button
                  onClick={() => {
                    setMainCharacterNameEditing(true);
                    setMainCharacterNameError('');
                    setMainCharacterNameSuccess('');
                  }}
                  className="text-cyan-400 hover:text-cyan-300 text-sm"
                >
                  Edit
                </button>
              )}
            </div>
            {mainCharacterNameEditing ? (
              <form onSubmit={handleMainCharacterNameUpdate} className="space-y-2">
                {mainCharacterNameError && (
                  <div className="text-red-400 text-sm">{mainCharacterNameError}</div>
                )}
                {mainCharacterNameSuccess && (
                  <div className="text-green-400 text-sm">{mainCharacterNameSuccess}</div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={mainCharacterName}
                    onChange={e => setMainCharacterName(e.target.value)}
                    className="flex-1 p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    placeholder="Enter main character name"
                    required
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 btn-neon rounded font-semibold"
                    disabled={mainCharacterNameLoading || !mainCharacterName.trim()}
                  >
                    {mainCharacterNameLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMainCharacterNameEditing(false);
                      setMainCharacterNameError('');
                      setMainCharacterNameSuccess('');
                      // Reset to current value from user object
                      const db = getFirestore(app);
                      getDoc(doc(db, 'users', user.uid)).then(userDoc => {
                        if (userDoc.exists()) {
                          setMainCharacterName(userDoc.data().mainCharacterName || '');
                        }
                      });
                    }}
                    className="px-4 py-2 glass rounded font-semibold hover:bg-surface-light"
                    disabled={mainCharacterNameLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-text-primary">
                {user.mainCharacterName || mainCharacterName || <span className="text-text-secondary italic">Not set</span>}
              </div>
            )}
          </div>
        </div>
        {/* Tracked Characters Section */}
        <div className="glass p-8 rounded-xl mb-8 w-full">
          <h2 className="text-2xl font-semibold mb-4">Tracked Characters</h2>
          {trackedLoading ? (
            <div>Loading tracked characters...</div>
          ) : tracked.length === 0 ? (
            <div className="text-text-secondary">You are not tracking any characters yet.</div>
          ) : (
            <div className="flex flex-col gap-4">
              {tracked.map((char) => (
                <div key={char.name} className="glass-enhanced rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 shadow-lg">
                  <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="font-bold text-lg text-cyan-300 mr-2">{char.name}</span>
                    <span className="text-text-secondary">{char.class}</span>
                    <span className="text-text-secondary">{char.spec}</span>
                    <span className={`font-semibold text-cyan-400`}>{char.team.charAt(0).toUpperCase() + char.team.slice(1)}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 sm:mt-0">
                    <div className="flex flex-col items-end gap-1">
                      <span className="neon-cyan-text font-bold">Mythic+ {char.mythicPlusThisWeek ?? 0}/8</span>
                      <span className="text-sm text-cyan-300">Weeks Met: {char.weeksMetRequirement ?? 0}</span>
                    </div>
                    <button
                      className="text-red-400 hover:text-red-600 font-semibold ml-2"
                      onClick={() => handleRemoveTracked(char.name, char.realm, char.region)}
                      disabled={removing === `${char.name}|${char.realm}|${char.region}`}
                    >
                      {removing === `${char.name}|${char.realm}|${char.region}` ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="glass p-8 rounded-xl">
          <h2 className="text-2xl font-semibold mb-4">Change Password</h2>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            {error && <div className="text-red-400 text-center mb-2">{error}</div>}
            {success && <div className="text-green-400 text-center mb-2">{success}</div>}
            <div>
              <label className="block mb-1 font-medium">Current Password</label>
              <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full p-2 rounded bg-surface-light border border-border" required />
            </div>
            <div>
              <label className="block mb-1 font-medium">New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full p-2 rounded bg-surface-light border border-border" required />
            </div>
            <div>
              <label className="block mb-1 font-medium">Confirm New Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full p-2 rounded bg-surface-light border border-border" required />
            </div>
            <button type="submit" className="w-full btn-neon py-2 rounded font-bold mt-2" disabled={passwordLoading}>{passwordLoading ? 'Updating...' : 'Change Password'}</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Account; 